import React, { useState } from "react";
import { Package, Users as UsersIcon, Map, Calculator, FileOutput, Shield, ClipboardList } from "lucide-react";

import { ProductsTab } from "./settings/ProductsTab";
import { CategoriesTab } from "./settings/CategoriesTab";
import { RepairPartsTab } from "./settings/RepairPartsTab";
import { AccessHireTab } from "./settings/AccessHireTab";
import { CustomersTab } from "./settings/CustomersTab";
import { BranchesTab } from "./settings/BranchesTab";
import { PostcodesTab } from "./settings/PostcodesTab";
import { MarkupsTab } from "./settings/MarkupsTab";
import { ExtraCostsTab } from "./settings/ExtraCostsTab";
import { FieldRequirementsTab } from "./settings/FieldRequirementsTab";
import { PdfBuilderTab } from "./settings/PdfBuilderTab";
import { LoginPageTab } from "./settings/LoginPageTab";
import { AccessControlTab } from "./settings/AccessControlTab";
import { AuditLogTab } from "./settings/AuditLogTab";
import { CostSettingsTab } from "./settings/CostSettingsTab";
import { StorageHealthTab } from "./settings/StorageHealthTab";
import { SurveyTemplatesTab } from "./settings/SurveyTemplatesTab";
import { HealthSafetyTab } from "./settings/HealthSafetyTab";
import { FloorAuditTab } from "./settings/FloorAuditTab";
import { SuppliersTab } from "./settings/SuppliersTab";
import { QuoteApprovalsTab } from "./settings/QuoteApprovalsTab";
import { PageAccessTab } from "./settings/PageAccessTab";

// Settings are grouped into a left-rail sub-menu so admins don't see 16 tabs
// stacked on two rows. Order = most-used first.
const GROUPS = [
  {
    key: "catalog", label: "Catalogue", icon: Package,
    desc: "Products & rules the quote builder picks from.",
    tabs: ["Products", "Categories", "Repair Parts", "Access Hire", "Suppliers"],
  },
  {
    key: "surveys", label: "Surveys", icon: ClipboardList,
    desc: "Templates surveyors use to capture site info.",
    tabs: ["Survey Templates", "Health & Safety"],
  },
  {
    key: "customers", label: "Customers", icon: UsersIcon,
    desc: "End customers and key account clients with default markups.",
    tabs: ["Customers", "Key Account Clients"],
  },
  {
    key: "locations", label: "Branches & travel", icon: Map,
    desc: "Branches, depots, postcode → travel-time map.",
    tabs: ["Branches", "Postcodes"],
  },
  {
    key: "pricing", label: "Pricing rules", icon: Calculator,
    desc: "Markup tiers, sundry/admin/contingency rules, internal cost rate.",
    tabs: ["Markups", "Extra Costs", "Costs", "Floor Audit"],
  },
  {
    key: "output", label: "Output", icon: FileOutput,
    desc: "What appears on the quote PDF and what's required to save.",
    tabs: ["PDF Builder", "Field Requirements", "Quote Approvals"],
  },
  {
    key: "system", label: "System", icon: Shield,
    desc: "Login page, IP restrictions, security audit log.",
    tabs: ["Login Page", "Access Control", "Page Access", "Audit Log", "Storage"],
  },
];

export default function CatalogPage() {
  const [tab, setTab] = useState("Products");
  // Determine active group from current tab (single source of truth).
  const activeGroup = GROUPS.find((g) => g.tabs.includes(tab)) || GROUPS[0];

  return (
    <div className="space-y-6 fade-up" data-testid="catalog-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">Settings</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Settings</h1>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Left rail — groups */}
        <nav className="space-y-1 lg:sticky lg:top-4 self-start" data-testid="settings-nav">
          {GROUPS.map((g) => {
            const Icon = g.icon;
            const isActive = activeGroup.key === g.key;
            return (
              <button
                key={g.key}
                onClick={() => setTab(g.tabs[0])}
                data-testid={`group-${g.key}`}
                className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? "bg-slate-900 text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                <Icon size={16} className={`mt-0.5 shrink-0 ${isActive ? "text-brand-orange" : "text-slate-500"}`} />
                <div className="min-w-0">
                  <div className="text-[12px] uppercase tracking-wider font-bold leading-tight">{g.label}</div>
                  <div className={`text-[11px] leading-snug mt-0.5 ${isActive ? "text-white/70" : "text-slate-500"}`}>
                    {g.desc}
                  </div>
                </div>
              </button>
            );
          })}
        </nav>

        {/* Right column — sub-tabs + content */}
        <div className="space-y-5 min-w-0">
          <div className="flex border-b-2 border-slate-900 overflow-x-auto no-scrollbar -mx-4 px-4 lg:mx-0 lg:px-0 lg:flex-wrap" data-testid="settings-subtabs">
            {activeGroup.tabs.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 sm:px-5 h-10 text-xs uppercase tracking-wider font-semibold border-x border-t -mb-px whitespace-nowrap flex-shrink-0 ${
                  tab === t ? "bg-slate-900 text-white border-slate-900" : "border-transparent text-slate-600 hover:text-slate-900"
                }`}
              >{t}</button>
            ))}
          </div>
          {tab === "Products" && <ProductsTab />}
          {tab === "Categories" && <CategoriesTab />}
          {tab === "Repair Parts" && <RepairPartsTab />}
          {tab === "Access Hire" && <AccessHireTab />}
          {tab === "Survey Templates" && <SurveyTemplatesTab />}
          {tab === "Health & Safety" && <HealthSafetyTab />}
          {tab === "Customers" && <CustomersTab />}
          {tab === "Key Account Clients" && <BranchesTab kind="key_account" />}
          {tab === "Branches" && <BranchesTab kind="depot" />}
          {tab === "Postcodes" && <PostcodesTab />}
          {tab === "Markups" && <MarkupsTab />}
          {tab === "Extra Costs" && <ExtraCostsTab />}
          {tab === "Field Requirements" && <FieldRequirementsTab />}
          {tab === "PDF Builder" && <PdfBuilderTab />}
          {tab === "Login Page" && <LoginPageTab />}
          {tab === "Access Control" && <AccessControlTab />}
          {tab === "Audit Log" && <AuditLogTab />}
          {tab === "Storage" && <StorageHealthTab />}
          {tab === "Costs" && <CostSettingsTab />}
          {tab === "Floor Audit" && <FloorAuditTab />}
          {tab === "Suppliers" && <SuppliersTab />}
          {tab === "Quote Approvals" && <QuoteApprovalsTab />}
          {tab === "Page Access" && <PageAccessTab />}
        </div>
      </div>
    </div>
  );
}
